/**
 * @file Vue composable for initializing carousel logic in a component.
 */

import { PausableTimer } from '@@/bits/pausable_timer'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

/**
 * Represents a slide in the carousel. The title is used to identify the slide for accessibility in slide controls.
 */
interface Slide {
  title: string
}

/**
 * Props for the carousel logic initialization.
 */
interface CarouselProps {
  slides: Slide[]
  loop?: boolean
  autoTransition?: boolean
  autoTransitionTimeInMs?: number
  onTransitionToPreviousSlide?: () => void
  onTransitionToNextSlide?: () => void
}

/**
 * @description A carousel has slide controls that allows for users to navigate to the previous slide, next slide, and pause/play the carousel.
 */
interface Carousel {
  currentSlideIndex: Ref<number>
  numberOfSlides: Ref<number>
  lastSlideIndex: Ref<number>
  prevSlideIndex: Ref<number>
  nextSlideIndex: Ref<number>
  isAtFirstSlide: Ref<boolean>
  isAtLastSlide: Ref<boolean>
  isAutoplaying: Ref<boolean>

  transitToPreviousSlide: () => void
  transitToNextSlide: () => void

  startAutoplay: () => void
  pauseAutoplay: () => void
  /** This function is used to start and resume autoplay. On resuming, the timer is reset. */
  startAutoplayIfUnpaused: () => void
  toggleAutoplay: () => void
}

const DEFAULT_TRANSITION_DELAY = 5000
export const useCarousel = ({
  slides,
  onTransitionToPreviousSlide,
  onTransitionToNextSlide,
  loop = true,
  autoTransition = true,
  autoTransitionTimeInMs = DEFAULT_TRANSITION_DELAY,
}: CarouselProps): Carousel => {
  const currentSlideIndex = ref<number>(0)
  const numberOfSlides = computed(() => slides.length)
  const lastSlideIndex = computed(() => numberOfSlides.value - 1)
  const prevSlideIndex = computed(() => currentSlideIndex.value - 1)
  const nextSlideIndex = computed(() => currentSlideIndex.value + 1)
  const isAtFirstSlide = computed(() => currentSlideIndex.value === 0)
  const isAtLastSlide = computed(() => currentSlideIndex.value === lastSlideIndex.value)

  const transitToNextSlide = (): void => {
    onTransitionToNextSlide?.()
    if (isAtLastSlide.value) {
      if (!loop) return
      currentSlideIndex.value = 0
    } else {
      currentSlideIndex.value = nextSlideIndex.value
    }
  }

  const transitToPreviousSlide = (): void => {
    onTransitionToPreviousSlide?.()

    if (isAtFirstSlide.value) {
      if (!loop) return
      currentSlideIndex.value = lastSlideIndex.value
    } else {
      currentSlideIndex.value = prevSlideIndex.value
    }
  }

  /**
   * This is the "engine" behind autoplay.
   * The autoplay timer ends after progressing a page, and we start the timer again if autoplay is still enabled.
   */
  const autoplayTimer = new PausableTimer()
  const isAutoplaying = ref(autoTransition)

  const resetAutoplayTimer = (): void => {
    autoplayTimer.setTimeoutDuration(autoTransitionTimeInMs)
    autoplayTimer.setOnComplete(transitToNextSlide)
    autoplayTimer.start()
  }

  const startAutoplay = (): void => {
    isAutoplaying.value = true
    resetAutoplayTimer()
  }

  const pauseAutoplay = (): void => {
    isAutoplaying.value = false
    autoplayTimer.pause()
  }

  const startAutoplayIfUnpaused = (): void => {
    if (isAutoplaying.value) {
      startAutoplay()
    }
  }

  const toggleAutoplay = (): void => {
    if (isAutoplaying.value) {
      pauseAutoplay()
    } else {
      startAutoplay()
    }
  }

  watch(currentSlideIndex, () => {
    startAutoplayIfUnpaused()
  })

  return {
    currentSlideIndex,
    numberOfSlides,
    lastSlideIndex,
    prevSlideIndex,
    nextSlideIndex,
    isAtFirstSlide,
    isAtLastSlide,
    isAutoplaying,

    transitToPreviousSlide,
    transitToNextSlide,

    startAutoplay,
    pauseAutoplay,
    startAutoplayIfUnpaused,
    toggleAutoplay,
  }
}
